import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill, HealthDot } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { displayName } from "@/utils/textFormat";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/common/TablePagination";
import { FilterChips } from "@/components/common/FilterChips";
import { getServices, getServiceCategories, getBusinessUnits, pickServicesList, productLabel } from "@/lib/masterCache";
// Phase 3 — passive commercial-payment policy badge derivation.
import { getCommercialPolicyBadge, COMMERCIAL_POLICY_BADGE_CLASSES } from "@/utils/commercialPolicyBadge";

const CASE_STATUSES = ["CREATED", "ACTIVE", "BLOCKED", "ON_HOLD", "BLOCKED_DEPENDENCY", "FINALIZED", "CLOSED", "CANCELLED", "REOPENED"];

/** Map dashboard drill-down query params → server filter shape. */

// L-2 · Dashboard drill-down `?filter=<bucket>` parser. Translates the
// canonical bucket names emitted by Operations / Owner-v2 dashboards into
// the existing taxonomy that `Cases.jsx` already understands. Unknown values
// are dropped silently so the page falls back to the default Cases view.
//
//   sla_breached → health=CRITICAL  (matches the `sla_breach` report which
//                                    filters by health_status=CRITICAL)
//   at_risk      → health=WARNING + status=ACTIVE  (renewals / SLA risk
//                                                   register — pre-breach)
//   unassigned   → status=ACTIVE + unassignedOnly client-side filter
const DASHBOARD_FILTERS = new Set(["sla_breached", "at_risk", "unassigned"]);

function parseQuery(search) {
  const sp = new URLSearchParams(search);
  const filterBucket = (sp.get("filter") || "").trim();
  const bucket = DASHBOARD_FILTERS.has(filterBucket) ? filterBucket : "";
  // Allow the bucket alias to set health/status defaults *only when the user
  // hasn't already pinned an explicit value via the same URL*. Explicit URL
  // params always win so the chip cluster keeps reflecting reality.
  const rawStatus = sp.get("status") || "";
  const rawHealth = sp.get("health") || sp.get("priority") || "";
  let derivedStatus = rawStatus;
  let derivedHealth = rawHealth;
  // Phase DASH-DRILLDOWN-PARITY (2026-07-09) — `sla_breached` no longer
  // translates to `health=CRITICAL`. The canonical semantics live on the
  // backend as `?filter=sla_breached` (due_date<today, non-terminal).
  if (bucket === "at_risk") {
    if (!rawHealth) derivedHealth = "WARNING";
    if (!rawStatus) derivedStatus = "ACTIVE";
  }
  if (bucket === "unassigned" && !rawStatus) derivedStatus = "ACTIVE";
  return {
    q: sp.get("search") || sp.get("q") || "",
    status: derivedStatus,
    health: derivedHealth,
    slaBreached: bucket === "sla_breached",  // NEW canonical flag
    businessUnitId: sp.get("business_unit_id") || sp.get("buId") || "",
    serviceId: sp.get("service_id") || "",
    serviceCategoryId: sp.get("service_category_id") || "",
    serviceSubTypeId: sp.get("service_sub_type_id") || "",
    blocked: sp.get("blocked") === "true",
    mine: sp.get("mine") === "true",
    createdFrom: sp.get("created_from") || "",
    createdTo: sp.get("created_to") || "",
    sla: sp.get("sla") || "",
    documentGate: sp.get("documentGate") || sp.get("gate") || "",
    groupBy: sp.get("groupBy") || "",
    // Team Workload drill-down accepts EITHER `assignee` or `ownerId`
    // (both map to the same case.assigned_to_user_id filter on the backend).
    assignee: sp.get("assignee") || sp.get("ownerId") || "",
    taskStatus: sp.get("taskStatus") || "",
    sourceTile: sp.get("sourceTile") || "",
    // Phase CASE-FINANCE
    invoiceStatus: sp.get("invoice_status") || "",
    paymentGate: sp.get("payment_gate_status") || "",
    hasOutstanding: sp.get("has_outstanding") === "true",
    paymentBlocked: sp.get("payment_blocked") === "true",
    overdueInvoice: sp.get("overdue_invoice") === "true",
    notInvoiced: sp.get("not_invoiced") === "true",
    // L-2 · Dashboard drill-down bucket (display + client-side filter).
    dashboardFilter: bucket,
  };
}

function serialize(state) {
  const sp = new URLSearchParams();
  if (state.q) sp.set("search", state.q);
  if (state.status) sp.set("status", state.status);
  if (state.health) sp.set("health", state.health);
  if (state.businessUnitId) sp.set("business_unit_id", state.businessUnitId);
  if (state.blocked) sp.set("blocked", "true");
  if (state.sla) sp.set("sla", state.sla);
  if (state.documentGate) sp.set("documentGate", state.documentGate);
  if (state.groupBy) sp.set("groupBy", state.groupBy);
  if (state.assignee) sp.set("assignee", state.assignee);
  if (state.serviceId) sp.set("service_id", state.serviceId);
  if (state.serviceCategoryId) sp.set("service_category_id", state.serviceCategoryId);
  if (state.serviceSubTypeId) sp.set("service_sub_type_id", state.serviceSubTypeId);
  if (state.mine) sp.set("mine", "true");
  if (state.createdFrom) sp.set("created_from", state.createdFrom);
  if (state.createdTo) sp.set("created_to", state.createdTo);
  if (state.taskStatus) sp.set("taskStatus", state.taskStatus);
  if (state.sourceTile) sp.set("sourceTile", state.sourceTile);
  // Phase CASE-FINANCE
  if (state.invoiceStatus) sp.set("invoice_status", state.invoiceStatus);
  if (state.paymentGate) sp.set("payment_gate_status", state.paymentGate);
  if (state.hasOutstanding) sp.set("has_outstanding", "true");
  if (state.paymentBlocked) sp.set("payment_blocked", "true");
  if (state.overdueInvoice) sp.set("overdue_invoice", "true");
  if (state.notInvoiced) sp.set("not_invoiced", "true");
  return sp.toString();
}

// Phase-D D3 hotfix · taxonomy filter param family.
// Only ONE of these may be present at a time. Sidebar clicks always strip
// the other two so we never carry a stale subtype past a category click etc.
const TAXONOMY_PARAMS = ["service_category_id", "service_id", "service_sub_type_id"];

/**
 * Build a Cases-page URL with exactly one taxonomy filter family applied.
 * - Strips ALL existing taxonomy params from `currentSearch` (so old subtype
 *   never bleeds past a fresh service / category click).
 * - Preserves explicitly-listed dashboard-context params from `currentSearch`
 *   (sourceTile, sourceDashboard, etc.). Pass [] to drop them entirely.
 * - Returns a path string suitable for `<NavLink to=…>` / `navigate(…)`.
 *
 * Exported for OperationsCasesTree.
 */
export function buildCaseTaxonomyUrl(
  { categoryId, serviceId, subTypeId } = {},
  currentSearch = "",
  preserveParams = [
    // Dashboard drill-down context
    "sourceTile", "sourceDashboard",
    // Unrelated filters that must survive a sidebar taxonomy click
    "status", "assignee", "ownerId", "sla", "blocked",
  ],
) {
  const sp = new URLSearchParams(currentSearch);
  TAXONOMY_PARAMS.forEach((k) => sp.delete(k));
  Array.from(sp.keys()).forEach((k) => {
    if (!preserveParams.includes(k)) sp.delete(k);
  });
  if (subTypeId) sp.set("service_sub_type_id", subTypeId);
  else if (serviceId) sp.set("service_id", serviceId);
  else if (categoryId) sp.set("service_category_id", categoryId);
  const qs = sp.toString();
  return qs ? `/cases?${qs}` : "/cases";
}

export default function Cases() {
  const nav = useNavigate();
  const location = useLocation();
  const initial = parseQuery(location.search);
  const [rows, setRows] = useState([]);
  const [allLoaded, setAllLoaded] = useState(false);
  const [q, setQ] = useState(initial.q);
  const [status, setStatus] = useState(initial.status);
  const [health, setHealth] = useState(initial.health);
  const [businessUnitId, setBusinessUnitId] = useState(initial.businessUnitId);
  const [blocked, setBlocked] = useState(initial.blocked);
  const [sla, setSla] = useState(initial.sla);
  const [documentGate, setDocumentGate] = useState(initial.documentGate);
  const [sourceTile, setSourceTile] = useState(initial.sourceTile);
  const [assignee, setAssignee] = useState(initial.assignee);
  const [mine, setMine] = useState(initial.mine || false);
  const [createdFrom, setCreatedFrom] = useState(initial.createdFrom || "");
  const [createdTo, setCreatedTo] = useState(initial.createdTo || "");
  // Phase CASE-FINANCE — finance filter state
  const [financeFilter, setFinanceFilter] = useState(() => {
    if (initial.paymentBlocked) return "blocked";
    if (initial.overdueInvoice) return "overdue";
    if (initial.hasOutstanding) return "outstanding";
    if (initial.notInvoiced) return "not_invoiced";
    return "";
  });
  const [serviceId, setServiceId] = useState(initial.serviceId);
  const [serviceCategoryId, setServiceCategoryId] = useState(initial.serviceCategoryId);
  const [serviceSubTypeId, setServiceSubTypeId] = useState(initial.serviceSubTypeId);
  const [bus, setBus] = useState([]);
  const [services, setServices] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  // Phase-D nav drilldown labels for the chip
  const [categories, setCategories] = useState([]);
  // L-2 · Dashboard drill-down bucket — controls the chip + client-side
  // filter (for `unassigned` which has no native backend param).
  const [dashboardFilter, setDashboardFilter] = useState(initial.dashboardFilter || "");

  // Track if any filter is active so we can show distinct empty state
  const anyFilter = !!(q || status || health || businessUnitId || blocked || sla || documentGate || assignee || serviceId || serviceCategoryId || serviceSubTypeId || mine || createdFrom || createdTo || dashboardFilter);

  // Phase-D D3 hotfix · URL is the SINGLE source of truth for filtering.
  // `load()` accepts the raw URL search string and derives its server query
  // directly from it — never from React state. This kills the URL→state→URL
  // feedback loop that was causing the filter-flip bug.
  const load = async (searchStr) => {
    const src = parseQuery(searchStr ?? location.search);
    const p = new URLSearchParams();
    if (src.q) p.set("q", src.q);
    if (src.status) p.set("status", src.status);
    else if (src.blocked) p.set("status", "BLOCKED");
    if (src.health) p.set("health", src.health);
    if (src.businessUnitId) p.set("business_unit_id", src.businessUnitId);
    if (src.sla) p.set("sla", src.sla);
    if (src.documentGate) p.set("document_gate", src.documentGate);
    if (src.assignee) p.set("assignee", src.assignee);
    if (src.mine) p.set("mine", "true");
    if (src.serviceId) p.set("service_id", src.serviceId);
    if (src.serviceCategoryId) p.set("service_category_id", src.serviceCategoryId);
    if (src.serviceSubTypeId) p.set("service_sub_type_id", src.serviceSubTypeId);
    if (src.createdFrom) p.set("created_from", src.createdFrom);
    if (src.createdTo) p.set("created_to", src.createdTo);
    // Phase CASE-FINANCE — opt-in finance enrichment + filters. Always
    // include_finance so the chip cluster has data to render; filters
    // are only sent when active. Backend remains the single source of
    // truth for invoice/gate state.
    p.set("include_finance", "true");
    if (src.invoiceStatus) p.set("invoice_status", src.invoiceStatus);
    if (src.paymentGate) p.set("payment_gate_status", src.paymentGate);
    if (src.hasOutstanding === true) p.set("has_outstanding", "true");
    if (src.paymentBlocked === true) p.set("payment_blocked", "true");
    if (src.overdueInvoice === true) p.set("overdue_invoice", "true");
    if (src.notInvoiced === true) p.set("not_invoiced", "true");
    // Phase DASH-DRILLDOWN-PARITY (2026-07-09) — pass canonical SLA filter.
    if (src.slaBreached || src.dashboardFilter === "sla_breached") p.set("filter", "sla_breached");
    // L-2 · `filter=unassigned` has no native backend param — fetch a
    // larger page so the client-side empty-assignee filter has enough
    // rows to display a meaningful list.
    if (src.dashboardFilter === "unassigned") p.set("limit", "200");
    const r = await api.get(`/cases?${p.toString()}`);
    let fetched = r.data.data || [];
    // L-2 · Client-side narrowing for the `unassigned` bucket — backend
    // does not expose an "is unassigned" filter so we drop rows that
    // carry any assignee. Cases with NO consultant / ops mgr / reviewer
    // / doc controller are kept.
    if (src.dashboardFilter === "unassigned") {
      fetched = fetched.filter((c) => !(
        c.assigned_consultant_id || c.operations_manager_id ||
        c.compliance_reviewer_id || c.document_controller_id
      ));
    }
    setRows(fetched);
    setAllLoaded(true);
    // INTENTIONALLY no URL write-back — URL is upstream of fetch.
  };

  useEffect(() => {
    // Initial taxonomy / picker data only — the [location.search] effect
    // below handles the actual cases fetch (single source of truth).
    getBusinessUnits().then((d) => setBus(d || [])).catch(() => setBus([]));
    getServices().then((d) => setServices(pickServicesList(d))).catch(() => setServices([]));
    getServiceCategories().then((d) => setCategories(Array.isArray(d) ? d : (d?.categories || []))).catch(() => setCategories([]));
    // eslint-disable-next-line
  }, []);

  // Phase-2: load sub-types when service filter changes.
  useEffect(() => {
    if (!serviceId) { setSubTypes([]); return; }
    api.get(`/services/${serviceId}/sub-types`).then((r) => setSubTypes(r.data.data || [])).catch(() => setSubTypes([]));
  }, [serviceId]);

  // Re-derive controlled-input state from URL AND refetch whenever URL changes.
  // The fetch is driven from the URL string directly so it can't race with
  // queued setState() calls.
  useEffect(() => {
    const next = parseQuery(location.search);
    setQ(next.q); setStatus(next.status); setHealth(next.health);
    setBusinessUnitId(next.businessUnitId); setBlocked(next.blocked);
    setSla(next.sla); setDocumentGate(next.documentGate); setSourceTile(next.sourceTile);
    setAssignee(next.assignee); setServiceId(next.serviceId);
    setServiceCategoryId(next.serviceCategoryId);
    setServiceSubTypeId(next.serviceSubTypeId);
    setMine(next.mine); setCreatedFrom(next.createdFrom); setCreatedTo(next.createdTo);
    setDashboardFilter(next.dashboardFilter || "");
    // Phase CASE-FINANCE — sync finance preset selector from URL
    setFinanceFilter(
      next.paymentBlocked ? "blocked" :
      next.overdueInvoice ? "overdue" :
      next.hasOutstanding ? "outstanding" :
      next.notInvoiced ? "not_invoiced" : ""
    );
    load(location.search);
    // eslint-disable-next-line
  }, [location.search]);

  // ─── Navigation helpers (every filter mutation goes through `nav()`) ────

  const navToFilter = (mutator) => {
    const sp = new URLSearchParams(location.search);
    mutator(sp);
    const qs = sp.toString();
    nav({ pathname: "/cases", search: qs ? `?${qs}` : "" }, { replace: false });
  };

  // Bug-fix · Cases filter panel "Apply" did nothing because `load()` reads
  // the URL while the Selects only update React state. We now serialize the
  // current form state into the URL — the [location.search] effect then
  // refetches via `load(location.search)` as the single source of truth.
  // Non-form context params (sourceTile, sourceDashboard, sla, blocked,
  // gate/documentGate, mine, assignee, dates) survive an Apply.
  const applyFilters = () => {
    const sp = new URLSearchParams(location.search);
    // Form-controlled params we own — clear all variants first
    ["search", "q", "status", "health", "priority",
     "business_unit_id", "buId",
     "service_id", "service_sub_type_id", "service_category_id"
    ].forEach((k) => sp.delete(k));
    if (q) sp.set("search", q);
    if (status) sp.set("status", status);
    if (health) sp.set("health", health);
    if (businessUnitId) sp.set("business_unit_id", businessUnitId);
    // Taxonomy family — keep only the most specific selection in the URL
    // (mirrors `buildCaseTaxonomyUrl` semantics used by the sidebar).
    if (serviceSubTypeId) sp.set("service_sub_type_id", serviceSubTypeId);
    else if (serviceId) sp.set("service_id", serviceId);
    else if (serviceCategoryId) sp.set("service_category_id", serviceCategoryId);
    // Phase CASE-FINANCE — finance preset → URL params.
    ["payment_blocked", "overdue_invoice", "has_outstanding", "not_invoiced"].forEach((k) => sp.delete(k));
    if (financeFilter === "blocked") sp.set("payment_blocked", "true");
    else if (financeFilter === "overdue") sp.set("overdue_invoice", "true");
    else if (financeFilter === "outstanding") sp.set("has_outstanding", "true");
    else if (financeFilter === "not_invoiced") sp.set("not_invoiced", "true");
    const qs = sp.toString();
    nav({ pathname: "/cases", search: qs ? `?${qs}` : "" }, { replace: false });
  };

  const clearAll = () => {
    // Hard reset to /cases. Preserve nothing — even dashboard drill chips are
    // user-requested cleared at this point.
    nav({ pathname: "/cases", search: "" }, { replace: false });
  };

  const tileLabel = sourceTile ? sourceTile.replace(/-/g, " ") : null;
  const chips = [];
  // Phase-D D3 hotfix · every chip clear navigates to a fresh URL. URL effect
  // refetches + resyncs state — no setState race, no URL write-back.
  const clearParam = (param) => navToFilter((sp) => sp.delete(param));
  const clearAllTaxonomy = () => navToFilter((sp) => {
    sp.delete("service_category_id");
    sp.delete("service_id");
    sp.delete("service_sub_type_id");
  });
  if (status) chips.push({ key: "status", label: `Status: ${status}`, onClear: () => clearParam("status") });
  if (blocked) chips.push({ key: "blocked", label: "Blocked only", onClear: () => clearParam("blocked") });
  if (sla) chips.push({ key: "sla", label: `SLA: ${sla}`, onClear: () => clearParam("sla") });
  // L-2 · Dashboard-drill chip is the first chip and uses the amber tone
  // so users instantly see they arrived from an attention tile. Clearing
  // it ALSO strips the derived health/status so the page returns to a
  // clean default view rather than a half-applied filter.
  if (dashboardFilter) {
    const bucketLabel = {
      sla_breached: "SLA Breached (due date past today)",
      at_risk:      "At-Risk Cases (health: WARNING)",
      unassigned:   "Unassigned Cases (no consultant / reviewer)",
    }[dashboardFilter] || `Filter: ${dashboardFilter}`;
    chips.unshift({
      key: "dashboard-filter",
      label: bucketLabel,
      tone: "amber",
      testId: "cases-dashboard-filter-chip",
      onClear: () => navToFilter((sp) => {
        sp.delete("filter");
        // Strip the URL params that the bucket *implicitly* set so the
        // page falls back to its default unfiltered view.
        if (dashboardFilter === "sla_breached" || dashboardFilter === "at_risk") sp.delete("health");
        if (dashboardFilter === "at_risk" || dashboardFilter === "unassigned") sp.delete("status");
      }),
    });
  }
  if (documentGate) chips.push({ key: "gate", label: `Gate: ${documentGate}`, onClear: () => clearParam("documentGate") });
  if (health) chips.push({ key: "health", label: `Priority: ${health}`, onClear: () => clearParam("health") });
  // Resolve assignee UUID → name from the loaded rows so the chip never shows a UUID.
  const assigneeName = (() => {
    if (!assignee) return "";
    const hit = rows.find((c) => [c.assigned_consultant_id, c.operations_manager_id,
                                   c.compliance_reviewer_id, c.document_controller_id].includes(assignee));
    if (!hit) return "";
    if (hit.assigned_consultant_id === assignee) return hit.assigned_consultant_name || "";
    if (hit.operations_manager_id === assignee) return hit.operations_manager_name || "";
    if (hit.compliance_reviewer_id === assignee) return hit.compliance_reviewer_name || "";
    if (hit.document_controller_id === assignee) return hit.document_controller_name || "";
    return "";
  })();
  if (assignee) chips.push({ key: "assignee", label: `Assignee: ${assigneeName || "—"}`, onClear: () => navToFilter((sp) => { sp.delete("assignee"); sp.delete("ownerId"); }) });
  if (sourceTile) chips.push({ key: "source-tile", label: `Drill-down: ${tileLabel}`, tone: "amber", onClear: () => navToFilter((sp) => sp.delete("sourceTile")) });
  // Phase-D nav drill-down chips — clearing any taxonomy chip strips ALL
  // taxonomy params so we never carry a stale subtype past a category click.
  if (serviceCategoryId) {
    const cat = categories.find((c) => c.id === serviceCategoryId);
    chips.push({ key: "cat", testId: "cases-filter-chip-taxonomy", label: `Category: ${cat?.name || serviceCategoryId.slice(0, 6)}`, tone: "cyan", onClear: clearAllTaxonomy });
  }
  if (serviceId) {
    const svc = services.find((s) => s.id === serviceId);
    chips.push({ key: "svc", testId: "cases-filter-chip-taxonomy", label: `Product: ${productLabel(svc) || serviceId.slice(0, 6)}`, tone: "cyan", onClear: clearAllTaxonomy });
  }
  if (serviceSubTypeId) {
    const sub = subTypes.find((s) => s.id === serviceSubTypeId);
    chips.push({ key: "sub", testId: "cases-filter-chip-taxonomy", label: `Subcategory: ${sub?.name || serviceSubTypeId.slice(0, 6)}`, tone: "cyan", onClear: clearAllTaxonomy });
  }

  const pg = usePagination(rows, { initialPageSize: 25 });

  return (
    <div data-testid="cases-screen">
      <PageHeader
        title="Cases"
        subtitle={
          tileLabel
            ? `Drill-down from dashboard tile · ${tileLabel}`
            : "The central operating object connecting customers, workflow, documents, submissions and finance."
        }
      />

      {/* Filter chips for incoming drill-down + clear */}
      <FilterChips chips={chips} onClearAll={clearAll} testId="cases-active-chips" />

      <div className="co-card p-4 mb-4 flex gap-3 items-end flex-wrap" data-testid="cases-filters">
        <div className="flex-1 min-w-60"><Label>Search</Label><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }} placeholder="Case number, title..." data-testid="cases-search" /></div>
        {/* Status + Business Unit filters removed from the toolbar (2026-09-12) — state/URL params retained. */}
        <div>
          <Label>Health</Label>
          <Select value={health || "ALL"} onValueChange={(v) => setHealth(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="cases-health-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All</SelectItem>{["HEALTHY","WARNING","CRITICAL"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {/* Phase CASE-FINANCE — finance preset. Mutually exclusive with
            itself; clears when set to ALL. */}
        <div>
          <Label>Finance</Label>
          <Select value={financeFilter || "ALL"} onValueChange={(v) => setFinanceFilter(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="cases-finance-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="blocked">Payment blocked</SelectItem>
              <SelectItem value="outstanding">Outstanding</SelectItem>
              <SelectItem value="overdue">Overdue invoice</SelectItem>
              <SelectItem value="not_invoiced">Not invoiced</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Product</Label>
          <Select value={serviceId || "ALL"} onValueChange={(v) => { setServiceId(v === "ALL" ? "" : v); setServiceSubTypeId(""); }}>
            <SelectTrigger className="w-52" data-testid="cases-service-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All products</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{productLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Sub-type</Label>
          <Select
            value={serviceSubTypeId || "ALL"}
            onValueChange={(v) => setServiceSubTypeId(v === "ALL" ? "" : v)}
            disabled={!serviceId || subTypes.length === 0}
          >
            <SelectTrigger className="w-52" data-testid="cases-sub-type-filter">
              <SelectValue placeholder={!serviceId ? "Pick a product" : subTypes.length === 0 ? "No sub-types" : "All"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sub-types</SelectItem>
              {subTypes.map((s) => <SelectItem key={s.id} value={s.id}>{productLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={applyFilters} data-testid="cases-apply-filters">Apply</Button>
      </div>

      {/* Desktop / tablet ≥ md : full table.
           TABLE-STICKY-FIX (2026-07-08): the global
           `.data-table.sticky-thead` rule now pins the header to the
           card's scroll container (top: 0) instead of the viewport
           topbar — see `frontend/src/index.css`. `overflow-x-auto`
           restores horizontal scroll for the 9-column layout without
           displacing the header. */}
      <div className="co-card overflow-x-auto hidden md:block" data-testid="cases-table">
        <table className="data-table sticky-thead min-w-full text-sm">
          <thead>
            <tr>
              <th className="text-left case-number-cell">Case #</th>
              <th className="text-left case-title-cell">Title</th>
              <th className="text-left">Customer</th>
              <th className="text-left">BU</th>
              <th className="text-left">Product · Sub-type</th>
              <th className="text-left">Stage</th>
              <th className="text-left">Consultant</th>
              <th className="text-left">Priority</th>
              <th className="text-left status-cell">Status</th>
              <th className="text-left status-cell">Payment</th>
              <th className="text-left status-cell">Finance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pg.pageRows.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => nav(`/cases/${c.id}`)} data-testid={`case-row-${c.id}`}>
                <td className="font-mono text-xs font-semibold case-number-cell"><span className="inline-flex items-center gap-2"><HealthDot value={c.health_status} />{c.case_number}{(() => {
                  // Phase 3 — passive commercial-payment policy badge.
                  // Reads additive list-response fields populated by
                  // backend `list_cases` (no extra API call per row).
                  const b = getCommercialPolicyBadge({
                    payment_term_mode: c.commercial_payment_mode,
                    advance_percent_before_documentation: c.commercial_payment_advance_percent,
                    payment_due_days: c.commercial_payment_due_days,
                  });
                  if (!b) return null;
                  return (
                    <span
                      className={`inline-flex items-center px-1.5 py-0 text-[10px] rounded-full font-semibold border ${COMMERCIAL_POLICY_BADGE_CLASSES[b.tone] || ""}`}
                      title={b.title}
                      data-testid={`case-policy-badge-${c.id}`}
                    >
                      {b.label}
                    </span>
                  );
                })()}</span></td>
                <td className="font-semibold text-slate-800 case-title-cell"><span className="case-title-text" title={c.title}>{displayName(c.title)}</span></td>
                <td className="text-slate-600">{displayName(c.customer_name) || "—"}</td>
                <td className="text-xs text-slate-500" data-testid={`case-bu-${c.id}`}>{c.business_unit_code || "—"}</td>
                <td className="text-xs text-slate-600" data-testid={`case-sub-type-${c.id}`}>
                  <div className="text-slate-700 font-medium">{c.service_name || "—"}</div>
                  {c.service_sub_type_name ? <div className="text-[11px] text-slate-500">{c.service_sub_type_name}</div> : null}
                </td>
                <td className="text-slate-600">{c.current_stage_name || "—"}</td>
                <td className="text-slate-700 text-xs" data-testid={`case-consultant-${c.id}`}>{c.assigned_consultant_name || "—"}</td>
                <td><StatusPill value={c.priority} /></td>
                <td className="status-cell"><StatusPill value={c.status} /></td>
                <td className="status-cell"><StatusPill value={c.payment_status} /></td>
                {/* Phase CASE-FINANCE — compact finance badge. Only shows
                    when backend returned the optional `finance_badge` block.
                    Safe to be missing — row renders unchanged. */}
                <td className="status-cell" data-testid={`case-finance-badge-${c.id}`}>
                  {c.finance_badge ? (() => {
                    const fb = c.finance_badge;
                    const out = Number(fb.outstanding_amount || 0);
                    const canonical = (fb.canonical_finance_state || "").toUpperCase();
                    const br = fb.billing_readiness;
                    // Phase CD-CLEANUP — Cases-list Finance column
                    // collapsed to a single canonical chip + a single
                    // optional reason/amount line. The Payment column
                    // next to it carries the lifecycle state, so this
                    // cell is purely the commercial gate / blocker
                    // summary. No more stacked "Finance Warning" /
                    // "Finance Blocked" pills under the label.
                    const aedShort = (v) => `AED ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                    // Map canonical state → {chipLabel, chipTone, reason}
                    const cfg = (() => {
                      switch (canonical) {
                        case "PAID":
                        case "SETTLED":
                          return { chip: "Cleared", tone: "emerald", reason: "" };
                        case "OVERRIDDEN":
                          return { chip: "Override", tone: "violet", reason: "Override recorded" };
                        case "ISSUED_UNPAID":
                          return { chip: "Blocked", tone: "rose",
                                   reason: out > 0 ? `Payment pending · ${aedShort(out)}` : "Payment pending" };
                        case "PARTIALLY_PAID":
                          return { chip: "Blocked", tone: "rose",
                                   reason: out > 0 ? `Balance pending · ${aedShort(out)}` : "Balance pending" };
                        case "UNALLOCATED_RECEIPT":
                          return { chip: "Warning", tone: "amber", reason: "Receipt not allocated" };
                        case "DRAFT_INVOICE":
                          return { chip: "Warning", tone: "amber",
                                   reason: (br && br.ready === false) ? "Billing data incomplete" : "Draft invoice not issued" };
                        case "NO_INVOICE":
                          return { chip: "Warning", tone: "amber",
                                   reason: (br && br.ready === false) ? "Billing data incomplete" : "Invoice not generated" };
                        case "LEGACY_EXCEPTION":
                          return { chip: "Review", tone: "amber", reason: "Finance review required" };
                        default:
                          return { chip: "—", tone: "slate", reason: "" };
                      }
                    })();
                    const toneMap = {
                      emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      amber:   "bg-amber-50 text-amber-700 border-amber-200",
                      rose:    "bg-rose-50 text-rose-700 border-rose-200",
                      violet:  "bg-violet-50 text-violet-700 border-violet-200",
                      slate:   "bg-slate-100 text-slate-700 border-slate-200",
                    };
                    const tooltipParts = [
                      fb.blocker_label,
                      (br && br.ready === false && br.reason_label) ? br.reason_label : null,
                      out > 0 ? `Outstanding ${aedShort(out)}` : null,
                    ].filter(Boolean);
                    const tooltip = tooltipParts.join(" · ") || canonical || undefined;
                    return (
                      <div
                        className="inline-flex flex-col items-start gap-0.5 max-w-[220px]"
                        title={tooltip}
                        data-canonical-finance-state={canonical || undefined}
                      >
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded-full font-semibold border ${toneMap[cfg.tone] || toneMap.slate}`}>
                          {cfg.chip}
                        </span>
                        {cfg.reason ? (
                          <span className="text-[11px] text-slate-500 truncate max-w-[220px] whitespace-nowrap" data-testid={`case-finance-reason-${c.id}`}>
                            {cfg.reason}
                          </span>
                        ) : null}
                        {/* Phase ADVANCE-GATE follow-up — amber chip
                            on Stage 1 / Quotation rows whose advance
                            requirement is unmet. Backend enriches
                            `finance_badge.advance_pending` only when
                            the case is canonically blocked AND at
                            sequence_no <= 1, so no per-row API call
                            is needed from the frontend. */}
                        {fb.advance_pending ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full font-semibold border bg-amber-50 text-amber-800 border-amber-200"
                            title={fb.advance_tooltip || fb.advance_label}
                            data-testid={`case-advance-chip-${c.id}`}
                          >
                            {fb.advance_label}
                          </span>
                        ) : null}
                      </div>
                    );
                  })() : <span className="text-[10px] text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {allLoaded && !rows.length ? (
              <tr>
                <td colSpan="11" className="text-center py-10 text-slate-500" data-testid="cases-empty-state">
                  {anyFilter ? "No cases match the current filters." : "No cases yet."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <TablePagination {...pg} testId="cases-pagination" />
      </div>

      {/* Mobile < md : card list */}
      <div className="md:hidden space-y-3" data-testid="cases-card-list">
        {rows.map((c) => (
          <div key={c.id} className="co-card p-4 cursor-pointer" onClick={() => nav(`/cases/${c.id}`)} data-testid={`case-card-${c.id}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <HealthDot value={c.health_status} />
                <span className="font-mono text-xs font-bold text-slate-700">{c.case_number}</span>
                {c.business_unit_code ? <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{c.business_unit_code}</span> : null}
              </div>
              <StatusPill value={c.status} />
            </div>
            <div className="font-semibold text-slate-900 leading-tight">{c.title}</div>
            <div className="text-xs text-slate-500 mt-1 truncate">{c.customer_name || "—"} · {c.current_stage_name || "—"}</div>
            <div className="flex items-center gap-1 flex-wrap mt-2">
              <StatusPill value={c.priority} />
              <StatusPill value={c.payment_status} />
            </div>
          </div>
        ))}
        {allLoaded && !rows.length ? (
          <div className="co-card p-6 text-center text-slate-500" data-testid="cases-empty-state-mobile">
            {anyFilter ? "No cases match the current filters." : "No cases yet."}
          </div>
        ) : null}
      </div>
    </div>
  );
}
